import React, { useState, useEffect } from 'react';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { Loading } from '../components/Loading';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { itemService } from '../services/itemService';
import { Item } from '../types';
import { formatCurrency } from '../utils/currency';
import { showToast } from '../utils/toast';

export const Inventory: React.FC = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; itemId: number | null }>({
    isOpen: false,
    itemId: null,
  });
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    category: '',
    stock_status: '' as 'in_stock' | 'running_low' | 'out_of_stock' | '',
    min_price: '',
    max_price: '',
    sort_by: 'name' as 'name' | 'brand' | 'price' | 'quantity' | 'created_at',
    sort_order: 'asc' as 'asc' | 'desc',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    size: '',
    category: 'Tyre',
    standard_price: '',
    quantity: '',
  });

  const CATEGORIES = ['Tyre', 'Battery', 'Rims'];
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadItems();
  }, [currentPage, searchQuery, filters]);

  const loadItems = async () => {
    try {
      setLoading(true);
      const filterParams: any = {
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
      };
      if (filters.category) {
        filterParams.category = filters.category;
      }
      if (filters.stock_status) {
        filterParams.stock_status = filters.stock_status;
      }
      if (filters.min_price) {
        filterParams.min_price = parseFloat(filters.min_price);
      }
      if (filters.max_price) {
        filterParams.max_price = parseFloat(filters.max_price);
      }
      const response = await itemService.getItems(
        currentPage,
        20,
        searchQuery || undefined,
        filterParams
      );
      if (response.success && response.data) {
        setItems(response.data.items);
        setTotalPages(response.data.pagination.pages);
      } else {
        showToast.error(response.message || 'Failed to load items');
      }
    } catch (err: any) {
      if (err.response?.status !== 401 && err.response?.status !== 422) {
        showToast.error(err.message || 'Failed to load items');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data: {
        name: string;
        brand: string;
        size?: string;
        category: string;
        standard_price: number;
        quantity: number;
      } = {
        name: formData.name.trim(),
        brand: formData.brand.trim(),
        size: formData.size.trim() || undefined,
        category: formData.category,
        standard_price: parseFloat(formData.standard_price),
        quantity: parseInt(formData.quantity),
      };

      if (editingItem) {
        await itemService.updateItem(editingItem.id, data);
        showToast.success('Item updated successfully');
      } else {
        await itemService.createItem(data);
        showToast.success('Item created successfully');
      }

      resetForm();
      loadItems();
    } catch (err: any) {
      showToast.error(err.response?.data?.message || err.message || 'Failed to save item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id: number) => {
    setDeleteConfirm({ isOpen: true, itemId: id });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.itemId) return;

    try {
      await itemService.deleteItem(deleteConfirm.itemId);
      showToast.success('Item deleted successfully');
      setDeleteConfirm({ isOpen: false, itemId: null });
      loadItems();
    } catch (err: any) {
      showToast.error(err.response?.data?.message || err.message || 'Failed to delete item');
      setDeleteConfirm({ isOpen: false, itemId: null });
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm({ isOpen: false, itemId: null });
  };

  const handleEdit = (item: Item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      brand: item.brand,
      size: item.size || '',
      category: item.category || 'Tyre',
      standard_price: item.standard_price.toString(),
      quantity: item.quantity.toString(),
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      brand: '',
      size: '',
      category: 'Tyre',
      standard_price: '',
      quantity: '',
    });
    setEditingItem(null);
    setShowForm(false);
  };

  const getStockStatusColor = (status: string) => {
    switch (status) {
      case 'out_of_stock':
        return 'bg-red-100 text-red-800';
      case 'running_low':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_stock':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStockStatusText = (status: string) => {
    switch (status) {
      case 'out_of_stock':
        return 'Out of Stock';
      case 'running_low':
        return 'Running Low';
      case 'in_stock':
        return 'In Stock';
      default:
        return status;
    }
  };

  if (loading && items.length === 0) {
    return <Loading fullScreen text="Loading inventory..." />;
  }

  return (
    <div className="p-6">
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Item"
        message="Are you sure you want to delete this item? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        variant="danger"
      />
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Inventory Management</h1>
        <Button onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ Add Item'}
        </Button>
      </div>


      {showForm && (
        <div className="mb-6 flex justify-center">
          <div className="w-full max-w-2xl">
            <Card title={editingItem ? 'Edit Item' : 'Add New Item'} className="shadow-lg">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-6">
              <Input
                label="Item Name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
              <Input
                label="Brand"
                type="text"
                value={formData.brand}
                onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                required
              />
              <Input
                label="Size"
                type="text"
                value={formData.size}
                onChange={(e) => setFormData({ ...formData, size: e.target.value })}
              />
              <Select
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </Select>
              <Input
                label="Standard Price (Buying Cost) - Ksh"
                type="number"
                step="1"
                min="0"
                value={formData.standard_price}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only allow whole numbers
                  if (value === '' || /^\d+$/.test(value)) {
                    setFormData({ ...formData, standard_price: value });
                  }
                }}
                placeholder="Enter amount in Ksh (whole numbers only)"
                required
              />
              <Input
                label="Quantity"
                type="number"
                min="0"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                required
              />
                </div>
                <div className="flex space-x-3 pt-4 border-t border-gray-200">
                  <Button type="submit" disabled={submitting} className="flex-1">
                    {submitting ? 'Saving...' : editingItem ? 'Update Item' : 'Create Item'}
                  </Button>
                  <Button type="button" variant="secondary" onClick={resetForm} className="flex-1">
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        </div>
      )}

      <Card
        title="Inventory Items"
        headerAction={
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="secondary"
              onClick={() => setShowFilters(!showFilters)}
              size="sm"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            <Input
              type="text"
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-48"
            />
          </div>
        }
      >
        {/* Advanced Filters */}
        {showFilters && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Advanced Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={filters.category || ''}
                  onChange={(e) => {
                    setFilters({ ...filters, category: e.target.value });
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Categories</option>
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stock Status
                </label>
                <select
                  value={filters.stock_status}
                  onChange={(e) => {
                    setFilters({ ...filters, stock_status: e.target.value as any });
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="in_stock">In Stock</option>
                  <option value="running_low">Running Low</option>
                  <option value="out_of_stock">Out of Stock</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Price (Ksh)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={filters.min_price}
                  onChange={(e) => {
                    setFilters({ ...filters, min_price: e.target.value });
                    setCurrentPage(1);
                  }}
                  placeholder="Min"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Price (Ksh)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={filters.max_price}
                  onChange={(e) => {
                    setFilters({ ...filters, max_price: e.target.value });
                    setCurrentPage(1);
                  }}
                  placeholder="Max"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sort By
                </label>
                <select
                  value={filters.sort_by}
                  onChange={(e) => {
                    setFilters({ ...filters, sort_by: e.target.value as any });
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="name">Name</option>
                  <option value="brand">Brand</option>
                  <option value="price">Price</option>
                  <option value="quantity">Quantity</option>
                  <option value="created_at">Date Added</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Order
                </label>
                <select
                  value={filters.sort_order}
                  onChange={(e) => {
                    setFilters({ ...filters, sort_order: e.target.value as any });
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>
            </div>
            <div className="mt-4">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setFilters({
                    category: '',
                    stock_status: '',
                    min_price: '',
                    max_price: '',
                    sort_by: 'name',
                    sort_order: 'asc',
                  });
                  setCurrentPage(1);
                }}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <Loading text="Loading items..." />
        ) : items.length === 0 ? (
          <p className="text-gray-600 text-center py-8">No items found</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Brand
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Standard Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Selling Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.brand}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {item.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.size || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatCurrency(item.standard_price)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatCurrency(item.selling_price)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.quantity}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStockStatusColor(
                            item.stock_status
                          )}`}
                        >
                          {getStockStatusText(item.stock_status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <Button
                  variant="secondary"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="secondary"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};
